import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ShieldCheck, ArrowLeft, Plus, X, Pencil, Trash2, CheckCircle2, MapPin,
} from 'lucide-react';
import whsService from '../../../services/whs';
import type {
  RiskAssessment, RiskItem, RiskMatrix as RiskMatrixConfig, RiskAssessmentStatus, HierarchyControl,
} from '../../../types/whs';
import RiskMatrix, { levelForScore } from '../../../components/whs/RiskMatrix';
import {
  RiskBadge, Badge, RA_TYPE_LABEL, RA_STATUS_LABEL, RA_STATUS_CLASS,
  HAZARD_CATEGORIES, HAZARD_CATEGORY_LABEL, HIERARCHY_LABEL,
} from '../../../components/whs/whsConstants';
import WhsGate from '../../../components/whs/WhsGate';
import PageHeader from '../../../components/PageHeader';
import Button from '../../../components/ui/Button';
import userService from '../../../services/user';
import type { UserData } from '../../../services/user';
import { useOrgTimezone, formatLocalDateInTz } from '../../../utils/timezone';

const inputClass =
  'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

const RA_STATUS_KEYS = Object.keys(RA_STATUS_LABEL) as RiskAssessmentStatus[];
const HIERARCHY_KEYS = Object.keys(HIERARCHY_LABEL) as HierarchyControl[];

function userLabel(u: UserData): string {
  const name = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim();
  return name || u.username;
}

interface ItemForm {
  hazard: string;
  hazard_category: string;
  risk_description: string;
  existing_controls: string;
  control_measures: string;
  hierarchy_control: HierarchyControl | '';
  inherent_likelihood: number | null;
  inherent_consequence: number | null;
  residual_likelihood: number | null;
  residual_consequence: number | null;
  responsible_person_id: number | null;
  review_date: string;
}

const EMPTY_ITEM: ItemForm = {
  hazard: '',
  hazard_category: '',
  risk_description: '',
  existing_controls: '',
  control_measures: '',
  hierarchy_control: '',
  inherent_likelihood: null,
  inherent_consequence: null,
  residual_likelihood: null,
  residual_consequence: null,
  responsible_person_id: null,
  review_date: '',
};

function itemToForm(item: RiskItem): ItemForm {
  return {
    hazard: item.hazard ?? '',
    hazard_category: item.hazard_category ?? '',
    risk_description: item.risk_description ?? '',
    existing_controls: item.existing_controls ?? '',
    control_measures: item.control_measures ?? '',
    hierarchy_control: item.hierarchy_control ?? '',
    inherent_likelihood: item.inherent_likelihood,
    inherent_consequence: item.inherent_consequence,
    residual_likelihood: item.residual_likelihood,
    residual_consequence: item.residual_consequence,
    responsible_person_id: item.responsible_person_id,
    review_date: item.review_date ?? '',
  };
}

function RiskAssessmentDetailInner() {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const { id } = useParams();
  const raId = Number(id);

  const [ra, setRa] = useState<RiskAssessment | null>(null);
  const [matrix, setMatrix] = useState<RiskMatrixConfig | null>(null);
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviewDate, setReviewDate] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<ItemForm>(EMPTY_ITEM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await whsService.getRiskAssessment(raId);
    if (res.success) {
      setRa(res.data);
      setReviewDate(res.data.review_date ?? '');
    } else {
      setError(res.error.message);
    }
    setLoading(false);
  }, [raId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let active = true;
    whsService.getRiskMatrix().then((res) => {
      if (active && res.success) setMatrix(res.data);
    });
    userService.getUsers().then((res) => {
      if (active && res.success) setUsers(res.data);
    });
    return () => { active = false; };
  }, []);

  const patchAssessment = async (data: Record<string, unknown>) => {
    const res = await whsService.updateRiskAssessment(raId, data);
    if (res.success) await load();
    else setError(res.error.message);
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_ITEM);
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (item: RiskItem) => {
    setEditingId(item.id);
    setForm(itemToForm(item));
    setFormError(null);
    setModalOpen(true);
  };

  const handleSaveItem = async () => {
    if (!form.hazard.trim()) {
      setFormError('A hazard description is required.');
      return;
    }
    setSaving(true);
    setFormError(null);
    const payload = {
      hazard: form.hazard.trim(),
      hazard_category: form.hazard_category || null,
      risk_description: form.risk_description.trim() || null,
      existing_controls: form.existing_controls.trim() || null,
      control_measures: form.control_measures.trim() || null,
      hierarchy_control: form.hierarchy_control || null,
      inherent_likelihood: form.inherent_likelihood,
      inherent_consequence: form.inherent_consequence,
      residual_likelihood: form.residual_likelihood,
      residual_consequence: form.residual_consequence,
      responsible_person_id: form.responsible_person_id,
      review_date: form.review_date || null,
    };
    const res = editingId
      ? await whsService.updateRiskItem(editingId, payload)
      : await whsService.createRiskItem(raId, payload);
    setSaving(false);
    if (res.success) {
      setModalOpen(false);
      await load();
    } else {
      setFormError(res.error.message);
    }
  };

  const handleDeleteItem = async (itemId: number) => {
    if (!window.confirm('Delete this risk item?')) return;
    const res = await whsService.deleteRiskItem(itemId);
    if (res.success) await load();
    else setError(res.error.message);
  };

  const inherentLevel =
    form.inherent_likelihood && form.inherent_consequence && matrix
      ? levelForScore(form.inherent_likelihood * form.inherent_consequence, matrix)
      : null;
  const residualLevel =
    form.residual_likelihood && form.residual_consequence && matrix
      ? levelForScore(form.residual_likelihood * form.residual_consequence, matrix)
      : null;

  const scoreText = (l: number | null, c: number | null): string =>
    l != null && c != null ? `${l} × ${c} = ${l * c}` : '—';

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Risk Assessment"
        subtitle="Hazard register with inherent & residual risk scoring"
        icon={<ShieldCheck className="h-6 w-6 text-white" />}
        actionButtons={
          <Button size="sm" variant="secondary" onClick={() => navigate('/hr/whs/risk-assessments')}>
            <ArrowLeft className="h-4 w-4" /> Back to list
          </Button>
        }
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>
        )}

        {loading ? (
          <p className="text-sm text-neutral-400">Loading…</p>
        ) : !ra ? (
          <p className="text-sm text-neutral-400">Risk assessment not found.</p>
        ) : (
          <>
            {/* Header card */}
            <div className="rounded-xl border border-neutral-200 bg-white p-6 mb-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h2 className="text-xl font-bold text-neutral-900">{ra.title}</h2>
                    <Badge label={RA_STATUS_LABEL[ra.status]} className={RA_STATUS_CLASS[ra.status]} />
                    {ra.approved_at && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
                        <CheckCircle2 className="h-4 w-4" /> Approved
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-neutral-500 mt-1">{RA_TYPE_LABEL[ra.assessment_type]}</p>
                  {ra.reference_code && <p className="text-xs text-neutral-500 mt-1">Ref: {ra.reference_code}</p>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {!ra.approved_at && (
                    <Button size="sm" variant="success" onClick={() => patchAssessment({ approve: true })}>
                      <CheckCircle2 className="h-4 w-4" /> Approve
                    </Button>
                  )}
                  <select
                    value={ra.status}
                    onChange={(e) => patchAssessment({ status: e.target.value as RiskAssessmentStatus })}
                    className={`${inputClass} max-w-[180px]`}
                  >
                    {RA_STATUS_KEYS.map((s) => (
                      <option key={s} value={s}>{RA_STATUS_LABEL[s]}</option>
                    ))}
                  </select>
                </div>
              </div>

              {ra.activity && (
                <div className="mt-4">
                  <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Activity</p>
                  <p className="text-sm text-neutral-700 mt-1 whitespace-pre-wrap">{ra.activity}</p>
                </div>
              )}

              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Location</p>
                  <p className="text-neutral-700 mt-1 inline-flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5 text-neutral-400" />
                    {ra.farm_name || ra.location || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Assessment date</p>
                  <p className="text-neutral-700 mt-1">
                    {ra.assessment_date ? formatLocalDateInTz(ra.assessment_date, orgTz) : '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Review date</p>
                  <input
                    type="date"
                    value={reviewDate}
                    onChange={(e) => setReviewDate(e.target.value)}
                    onBlur={() => {
                      if (reviewDate !== (ra.review_date ?? '')) {
                        patchAssessment({ review_date: reviewDate || null });
                      }
                    }}
                    className={`${inputClass} mt-1`}
                  />
                </div>
                <div>
                  <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Assessed by</p>
                  <p className="text-neutral-700 mt-1">{ra.assessed_by ?? '—'}</p>
                </div>
              </div>
            </div>

            {/* Risk items */}
            <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
                <h3 className="text-sm font-semibold text-neutral-900">Risk items ({ra.items.length})</h3>
                <Button size="sm" onClick={openAdd}>
                  <Plus className="h-4 w-4" /> Add risk item
                </Button>
              </div>
              {ra.items.length === 0 ? (
                <p className="p-6 text-sm text-neutral-400">No risk items yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-neutral-200">
                    <thead className="bg-neutral-50">
                      <tr className="text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                        <th className="px-4 py-3">Hazard</th>
                        <th className="px-4 py-3">Inherent</th>
                        <th className="px-4 py-3">Controls</th>
                        <th className="px-4 py-3">Residual</th>
                        <th className="px-4 py-3">Responsible</th>
                        <th className="px-4 py-3">Review</th>
                        <th className="px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100 text-sm align-top">
                      {ra.items.map((item) => (
                        <tr key={item.id}>
                          <td className="px-4 py-3 max-w-xs">
                            <div className="font-medium text-neutral-900 whitespace-pre-wrap">{item.hazard}</div>
                            {item.hazard_category && (
                              <div className="text-xs text-neutral-500 mt-0.5">
                                {HAZARD_CATEGORY_LABEL[item.hazard_category] ?? item.hazard_category}
                              </div>
                            )}
                            {item.risk_description && (
                              <div className="text-xs text-neutral-500 mt-1 whitespace-pre-wrap">{item.risk_description}</div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-xs text-neutral-500 mb-1">
                              {scoreText(item.inherent_likelihood, item.inherent_consequence)}
                            </div>
                            <RiskBadge level={item.inherent_level} />
                          </td>
                          <td className="px-4 py-3 max-w-xs text-xs text-neutral-600">
                            {item.hierarchy_control && (
                              <div className="font-medium text-neutral-700">{HIERARCHY_LABEL[item.hierarchy_control]}</div>
                            )}
                            {item.existing_controls && (
                              <div className="mt-1 whitespace-pre-wrap">{item.existing_controls}</div>
                            )}
                            {item.control_measures && (
                              <div className="mt-1 whitespace-pre-wrap">{item.control_measures}</div>
                            )}
                            {!item.hierarchy_control && !item.existing_controls && !item.control_measures && '—'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-xs text-neutral-500 mb-1">
                              {scoreText(item.residual_likelihood, item.residual_consequence)}
                            </div>
                            <RiskBadge level={item.residual_level} />
                          </td>
                          <td className="px-4 py-3 text-neutral-600">
                            {`${item.responsible_first_name ?? ''} ${item.responsible_last_name ?? ''}`.trim() || '—'}
                          </td>
                          <td className="px-4 py-3 text-neutral-600">
                            {item.review_date ? formatLocalDateInTz(item.review_date, orgTz) : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => openEdit(item)}
                                className="p-1.5 rounded-lg text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700"
                                aria-label="Edit"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteItem(item.id)}
                                className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"
                                aria-label="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Risk item modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-3xl bg-white rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4 sticky top-0 bg-white">
              <h2 className="text-lg font-semibold text-neutral-900">
                {editingId ? 'Edit risk item' : 'Add risk item'}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-neutral-400 hover:text-neutral-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              {formError && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{formError}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Hazard <span className="text-red-500">*</span></label>
                <textarea
                  className={inputClass}
                  rows={2}
                  value={form.hazard}
                  onChange={(e) => setForm({ ...form, hazard: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Hazard category</label>
                  <select
                    className={inputClass}
                    value={form.hazard_category}
                    onChange={(e) => setForm({ ...form, hazard_category: e.target.value })}
                  >
                    <option value="">Select…</option>
                    {HAZARD_CATEGORIES.map((c) => (
                      <option key={c} value={c}>{HAZARD_CATEGORY_LABEL[c] ?? c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Hierarchy of control</label>
                  <select
                    className={inputClass}
                    value={form.hierarchy_control}
                    onChange={(e) => setForm({ ...form, hierarchy_control: e.target.value as HierarchyControl | '' })}
                  >
                    <option value="">Select…</option>
                    {HIERARCHY_KEYS.map((h) => (
                      <option key={h} value={h}>{HIERARCHY_LABEL[h]}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Risk description</label>
                <textarea
                  className={inputClass}
                  rows={2}
                  value={form.risk_description}
                  onChange={(e) => setForm({ ...form, risk_description: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Existing controls</label>
                <textarea
                  className={inputClass}
                  rows={2}
                  value={form.existing_controls}
                  onChange={(e) => setForm({ ...form, existing_controls: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Additional control measures</label>
                <textarea
                  className={inputClass}
                  rows={2}
                  value={form.control_measures}
                  onChange={(e) => setForm({ ...form, control_measures: e.target.value })}
                />
              </div>

              {/* Inherent + residual matrices */}
              {matrix && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-neutral-700">Inherent risk</label>
                      {inherentLevel && <RiskBadge level={inherentLevel} />}
                    </div>
                    <RiskMatrix
                      matrix={matrix}
                      compact
                      selectedLikelihood={form.inherent_likelihood}
                      selectedConsequence={form.inherent_consequence}
                      onSelect={(l, c) => setForm({ ...form, inherent_likelihood: l, inherent_consequence: c })}
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-neutral-700">Residual risk (after controls)</label>
                      {residualLevel && <RiskBadge level={residualLevel} />}
                    </div>
                    <RiskMatrix
                      matrix={matrix}
                      compact
                      selectedLikelihood={form.residual_likelihood}
                      selectedConsequence={form.residual_consequence}
                      onSelect={(l, c) => setForm({ ...form, residual_likelihood: l, residual_consequence: c })}
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Responsible person</label>
                  <select
                    className={inputClass}
                    value={form.responsible_person_id ?? ''}
                    onChange={(e) => setForm({ ...form, responsible_person_id: e.target.value ? Number(e.target.value) : null })}
                  >
                    <option value="">Unassigned</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{userLabel(u)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Review date</label>
                  <input
                    type="date"
                    className={inputClass}
                    value={form.review_date}
                    onChange={(e) => setForm({ ...form, review_date: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-neutral-200 px-5 py-4 sticky bottom-0 bg-white">
              <Button variant="secondary" size="sm" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={handleSaveItem} isLoading={saving}>
                {editingId ? 'Save changes' : 'Add item'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WhsRiskAssessmentDetail() {
  return <WhsGate><RiskAssessmentDetailInner /></WhsGate>;
}
